import React, { useState, useEffect, useRef } from "react";
import DeleteConfirmModal from "../components/DeleteConfirmModal";
import ProfilesTrafficModal, { getRedItems, getYellowItems, getGreenItems } from "../components/ProfilesTrafficModal";
import "./ManageProfiles.css";
import "./ManageSMRTDocs.css";
import Overview from "./Overview";
import TemplateEditor from "./TemplateEditor";
import { EmployeeTemplate } from "../profileTemplates/employee.template";
import {
  ProfileTemplate,
  ProfileField,
  isSystemTemplate,
} from "../profileTemplates/profileTemplate.model";
import { useAuth } from "../hooks/useAuth";

interface SensitiveAccessEvent {
  timestamp: string;
  userId: string;
  profileTemplateId: string;
  templateName: string;
  fieldKey: string;
  revealedByUserID: string;
  reason: string;
  ipAddress: string;
  userAgent: string;
}

// TODO: The DB currently stores templates in an old schema format where:
//   - steps use "title" instead of "label"
//   - steps have a "sections" array of { id: string } instead of a flat "fields" array
// The normalizeStep function below handles both formats for now.
// Once all documents are migrated to the new schema (label + fields), this can be removed.
const normalizeStep = (step: any) => ({
  scope: step.scope || "public",
  ...step,
  label: step.label ?? step.title ?? "Untitled Step",
  fields: (
    step.fields ??
    step.sections?.flatMap((s: any) => s.fields ?? []) ??
    []
  ).filter(Boolean),
});

export default function ManageProfiles() {
  const [users, setUsers] = useState<any[]>([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [usersError, setUsersError] = useState<string | null>(null);
  const [submissions, setSubmissions] = useState<any[]>([]);
  const [profileAssignments, setProfileAssignments] = useState<Record<string, string>>({});
  const [isTrafficModalOpen, setIsTrafficModalOpen] = useState(false);
  const [trafficFilter, setTrafficFilter] = useState<"red" | "yellow" | "green" | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{
    title: string;
    message: string;
    /** A refusal rather than a question — one dismiss button, no confirm. */
    blocked?: boolean;
    onConfirm: () => void;
  } | null>(null);
  const [renameModal, setRenameModal] = useState<{ index: number; value: string } | null>(null);

  const loadApprovedUsers = async () => {
    setUsersLoading(true);
    setUsersError(null);
    try {
      const [usersRes, rolesRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/approved_users`, { method: "GET", credentials: "include" }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/roles?scope=all`, { method: "GET", credentials: "include" }),
      ]);
      if (!usersRes.ok) throw new Error(`approved_users responded ${usersRes.status}`);
      const data = await usersRes.json();
      const rolesData = await rolesRes.json();
      const orgRoles = rolesData?.org || [];

      const profileRoleIDs = new Set(
        orgRoles
          .filter((r: any) => r.permissions?.includes('profiles.view') || r.permissions?.includes('profiles.admin'))
          .map((r: any) => r.id)
      );

      const filtered = (Array.isArray(data) ? data : []).filter((u: any) =>
        (u.customRoles || []).some((rid: string) => profileRoleIDs.has(rid))
      );

      console.log("APPROVED USERS count:", Array.isArray(data) ? data.length : 0, "| filtered:", filtered.length);
      setUsers(filtered);
    } catch (e) {
      console.error("Failed to load users", e);
      setUsersError("Couldn't load the user list. Please try again.");
    } finally {
      setUsersLoading(false);
    }
  };

  const loadProfileTemplates = async () => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/templates`,
        { method: "GET", credentials: "include" },
      );
      const data = await res.json();

      if (Array.isArray(data) && data.length > 0) {
        setTemplates(
          data.map((t: any) => ({
            id: t.id,
            name: t.name,
            data: {
              ...t.schema,
              steps: t.schema.steps.map(normalizeStep),
            },
          })),
        );
        setActiveTemplateIndex(0);
        return;
      }

      // No templates in DB — seed default Employee template
      const defaultTemplate = {
        name: "Employee",
        data: JSON.parse(JSON.stringify(EmployeeTemplate)),
      };

      setTemplates([defaultTemplate]);
      setActiveTemplateIndex(0);

      await fetch(`${process.env.REACT_APP_BACKEND_URL}/templates`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: defaultTemplate.name,
          schema: defaultTemplate.data,
        }),
      });
    } catch (e) {
      console.error("Failed to load templates", e);
    }
  };

  const [templates, setTemplates] = useState<
    { id?: string; name: string; data: ProfileTemplate }[]
  >([
    {
      name: "Employee",
      data: {
        ...JSON.parse(JSON.stringify(EmployeeTemplate)),
        steps: JSON.parse(JSON.stringify(EmployeeTemplate)).steps.map(
          normalizeStep,
        ),
      },
    },
  ]);

  const [activeTemplateIndex, setActiveTemplateIndex] = useState(0);
  const templatesRef = useRef(templates);
  templatesRef.current = templates;
  const [template, setTemplate] = useState<ProfileTemplate>(templates[0].data);

  useEffect(() => {
    setTemplate(
      JSON.parse(JSON.stringify(templates[activeTemplateIndex].data)),
    );
  }, [activeTemplateIndex, templates]);

  // ── Polling for traffic-light data (submissions + assignments) ─────────────
  const lastSubsHash = useRef("");
  const lastAssignHash = useRef("");

  const pollTrafficData = () => {
    Promise.all([
      fetch(`${process.env.REACT_APP_BACKEND_URL}/profile-submissions`, { credentials: "include" }).then(r => r.json()),
      fetch(`${process.env.REACT_APP_BACKEND_URL}/user-profile-assignments`, { credentials: "include" }).then(r => r.json()),
    ])
      .then(([subsData, assignData]) => {
        const subs = Array.isArray(subsData) ? subsData : [];
        const subsHash = JSON.stringify(subs);
        if (subsHash !== lastSubsHash.current) {
          setSubmissions(subs);
          lastSubsHash.current = subsHash;
        }

        const map: Record<string, string> = {};
        (assignData || []).forEach((a: any) => { map[a.userId] = a.profileTemplateId; });
        const assignHash = JSON.stringify(map);
        if (assignHash !== lastAssignHash.current) {
          setProfileAssignments(map);
          lastAssignHash.current = assignHash;
        }
      })
      .catch((err) => console.error("Failed to poll profile data", err));
  };

  useEffect(() => {
    loadApprovedUsers();
    loadProfileTemplates();
    pollTrafficData();

    let interval = setInterval(pollTrafficData, 12000);
    const onVisibility = () => {
      clearInterval(interval);
      if (document.visibilityState === "visible") {
        pollTrafficData();
        interval = setInterval(pollTrafficData, 12000);
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => { clearInterval(interval); document.removeEventListener("visibilitychange", onVisibility); };
  }, []);

  const [editingStepId, setEditingStepId] = useState<number | null>(null);
  const [snapshot, setSnapshot] = useState<ProfileTemplate | null>(null);
  const [dirty, setDirty] = useState(false);
  const [openFormatFields, setOpenFormatFields] = useState<
    Record<string, boolean>
  >({});
  const [openVisibilityFields, setOpenVisibilityFields] = useState<
    Record<string, boolean>
  >({});
  const [showNewTemplatePanel, setShowNewTemplatePanel] = useState(false);
  /** Set to "" when "Add New" is clicked — the name is asked for before creating. */
  const [newTemplateName, setNewTemplateName] = useState<string | null>(null);
  const [creatingTemplate, setCreatingTemplate] = useState(false);
  const [newTemplateError, setNewTemplateError] = useState<string | null>(null);
  /** Which template "Duplicate Existing" is copying — set when the modal opens. */
  const [duplicateModal, setDuplicateModal] = useState<{ sourceIndex: number } | null>(null);
  const [duplicating, setDuplicating] = useState(false);
  const [duplicateError, setDuplicateError] = useState<string | null>(null);
  /** Whether the added-template tab row is showing. Closed until asked for. */
  const [customTabsOpen, setCustomTabsOpen] = useState(false);
  /** Set when a step save fails — the last native dialog on this tab. */
  const [saveError, setSaveError] = useState<string | null>(null);
  /** The scope the new step will carry — set when "+ Add Step" opens the modal. */
  const [addStepModal, setAddStepModal] = useState<{
    scope: "public" | "internal";
  } | null>(null);
  const [activeTopTab, setActiveTopTab] = useState<
    "overview" | "templates" | "accessLog"
  >("overview");

  // Sensitive Access Log — visible to profiles.admin OR profiles.sensitive.
  // Fetched lazily when the tab is first opened so non-viewers (and the
  // common case where the tab is never clicked) pay zero network cost.
  const { roles } = useAuth();
  const canViewAccessLog =
    Array.isArray(roles?.permissions) &&
    (roles.permissions.includes("profiles.admin") ||
      roles.permissions.includes("profiles.sensitive"));
  const [accessLogEvents, setAccessLogEvents] = useState<
    SensitiveAccessEvent[]
  >([]);
  const [accessLogLoading, setAccessLogLoading] = useState(false);
  const [accessLogError, setAccessLogError] = useState<string | null>(null);
  const accessLogLoadedRef = useRef(false);

  useEffect(() => {
    if (activeTopTab !== "accessLog") return;
    if (accessLogLoadedRef.current) return;
    if (!canViewAccessLog) return;
    accessLogLoadedRef.current = true;
    setAccessLogLoading(true);
    setAccessLogError(null);
    fetch(
      `${process.env.REACT_APP_BACKEND_URL}/profile-submissions/sensitive-access-log`,
      { credentials: "include" },
    )
      .then(async (res) => {
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body?.error || `Failed (${res.status})`);
        }
        return res.json();
      })
      .then((data: SensitiveAccessEvent[]) => {
        setAccessLogEvents(Array.isArray(data) ? data : []);
      })
      .catch((err: any) => {
        setAccessLogError(err?.message || "Failed to load access log");
        accessLogLoadedRef.current = false; // allow retry on tab re-open
      })
      .finally(() => setAccessLogLoading(false));
  }, [activeTopTab, canViewAccessLog]);

  // "Add New": a template built from nothing.
  //
  // It opens on one empty public step rather than none, because a template with
  // no steps gives the editor nothing to render and no field to add — the step
  // is the container fields go in. Everything else is the admin's to write.
  const createBlankTemplate = async () => {
    const name = (newTemplateName || "").trim();
    if (!name || creatingTemplate) return;

    const blank: ProfileTemplate = {
      // The schema's own id is display-level only — the record's id is the one
      // Cosmos hands back — but a readable slug beats an empty string.
      id:
        name
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-|-$/g, "") || "template",
      name,
      version: 1,
      status: "active",
      steps: [{ id: 1, label: "New Step", scope: "public", fields: [] }],
    };

    setCreatingTemplate(true);
    setNewTemplateError(null);

    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/templates`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ name, schema: blank }),
        },
      );

      // Checked, so a 403 from profiles.admin can't land a template in the
      // list with no id behind it — it would look saved and never be.
      if (!res.ok) throw new Error(`Create failed (${res.status})`);
      const saved = await res.json();

      const next = [
        ...templatesRef.current,
        { id: saved.id, name, data: blank },
      ];
      setTemplates(next);
      setActiveTemplateIndex(next.length - 1);
      // The new template is now the one being edited, so its row has to be
      // showing — otherwise the editor opens on a tab that isn't on screen.
      setCustomTabsOpen(true);

      setNewTemplateName(null);
      setShowNewTemplatePanel(false);
    } catch (e) {
      console.error("Failed to create template", e);
      setNewTemplateError("Couldn't create the template. Please try again.");
    } finally {
      setCreatingTemplate(false);
    }
  };

  const duplicateTemplate = async () => {
    if (!duplicateModal || duplicating) return;
    const { sourceIndex } = duplicateModal;

    const source = templates[sourceIndex];
    if (!source) return;
    const newTemplate = {
      name: `${source.name} Copy`,
      data: JSON.parse(JSON.stringify(source.data)),
    };

    setDuplicating(true);
    setDuplicateError(null);

    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/templates`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: newTemplate.name,
            schema: newTemplate.data,
          }),
        },
      );

      // Checked, so a 403 can't land a template in the list with no id behind
      // it — it would look saved and never be.
      if (!res.ok) throw new Error(`Duplicate failed (${res.status})`);
      const saved = await res.json();

      const next = [
        ...templatesRef.current,
        { id: saved.id, name: newTemplate.name, data: newTemplate.data },
      ];
      setTemplates(next);
      setActiveTemplateIndex(next.length - 1);
      setCustomTabsOpen(true);

      setDuplicateModal(null);
      setShowNewTemplatePanel(false);
    } catch (e) {
      console.error("Failed to duplicate template", e);
      setDuplicateError("Couldn't duplicate the template. Please try again.");
    } finally {
      setDuplicating(false);
    }
  };

  /* ===== lifecycle ===== */

  const startEdit = (stepId: number) => {
    setSnapshot(JSON.parse(JSON.stringify(template)));
    setEditingStepId(stepId);
    setDirty(false);
  };

  const cancelEdit = () => {
    if (snapshot) setTemplate(snapshot);
    setEditingStepId(null);
    setSnapshot(null);
    setDirty(false);
  };

  const saveEdit = async () => {
    try {
      const updatedTemplate = JSON.parse(JSON.stringify(template));

      setTemplates((prev) =>
        prev.map((t, i) =>
          i === activeTemplateIndex ? { ...t, data: updatedTemplate } : t,
        ),
      );

      const current = templatesRef.current[activeTemplateIndex];

      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/templates`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: current.id,
          name: current.name,
          schema: updatedTemplate,
        }),
      });

      // Checked, so a rejected save can't close the editor and leave the
      // screen showing a change the server never took.
      if (!res.ok) throw new Error(`Save failed (${res.status})`);

      setEditingStepId(null);
      setSnapshot(null);
      setDirty(false);
    } catch (e) {
      console.error("Failed to save template", e);
      setSaveError("Couldn't save the template. Please try again.");
    }
  };

  /* ===== step helpers ===== */

  const updateStepLabel = (stepId: number, label: string) => {
    setDirty(true);
    setTemplate((prev) => ({
      ...prev,
      steps: prev.steps.map((step) =>
        step.id === stepId ? { ...step, label } : step,
      ),
    }));
  };

  const moveStep = (stepId: number, direction: "up" | "down") => {
    setDirty(true);
    setTemplate((prev) => {
      const index = prev.steps.findIndex((s) => s.id === stepId);
      if (
        (direction === "up" && index === 0) ||
        (direction === "down" && index === prev.steps.length - 1)
      )
        return prev;

      const steps = [...prev.steps];
      const swapIndex = direction === "up" ? index - 1 : index + 1;
      [steps[index], steps[swapIndex]] = [steps[swapIndex], steps[index]];

      // Reassign IDs sequentially to persist order
      const reordered = steps.map((s, i) => ({ ...s, id: i + 1 }));
      return { ...prev, steps: reordered };
    });
  };

  const addStep = () => {
    if (!addStepModal) return;
    const { scope } = addStepModal;
    setAddStepModal(null);

    setDirty(true);
    setTemplate((prev) => {
      const nextId =
        prev.steps.length > 0
          ? Math.max(...prev.steps.map((s) => s.id)) + 1
          : 1;
      return {
        ...prev,
        steps: [
          ...prev.steps,
          { id: nextId, label: "New Step", scope, fields: [] },
        ],
      };
    });
  };

  const deleteStep = (stepId: number) => {
    if (template.steps.length === 1) return;
    setDeleteConfirm({
      title: "Delete Step",
      message: "Delete this step and all its fields?",
      onConfirm: async () => {
        setDeleteConfirm(null);
        const updatedTemplate = {
          ...template,
          steps: template.steps.filter((s) => s.id !== stepId),
        };

        setTemplate(updatedTemplate);

        try {
          const current = templatesRef.current[activeTemplateIndex];
          await fetch(`${process.env.REACT_APP_BACKEND_URL}/templates`, {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              id: current.id,
              name: current.name,
              schema: updatedTemplate,
            }),
          });
        } catch (e) {
          console.error("Failed to persist step delete", e);
        }
      },
    });
  };

  /* ===== field helpers ===== */

  // Drag-to-reorder: move `fromKey` to just before/after `toKey` within the
  // same step (after = dropped on the lower half of the target row).
  const reorderField = (
    stepId: number,
    fromKey: string,
    toKey: string,
    after: boolean,
  ) => {
    if (fromKey === toKey) return;
    setDirty(true);
    setTemplate((prev) => ({
      ...prev,
      steps: prev.steps.map((step) => {
        if (step.id !== stepId) return step;
        const fields = [...step.fields];
        const fromIdx = fields.findIndex((f) => f.key === fromKey);
        if (fromIdx === -1) return step;
        const [moved] = fields.splice(fromIdx, 1);
        const toIdx = fields.findIndex((f) => f.key === toKey);
        if (toIdx === -1) return step;
        fields.splice(toIdx + (after ? 1 : 0), 0, moved);
        return { ...step, fields };
      }),
    }));
  };

  const updateField = (
    stepId: number,
    fieldKey: string,
    updates: Partial<ProfileField>,
  ) => {
    setDirty(true);
    setTemplate((prev) => ({
      ...prev,
      steps: prev.steps.map((step) =>
        step.id !== stepId
          ? step
          : {
              ...step,
              fields: step.fields.map((field) =>
                field.key !== fieldKey ? field : { ...field, ...updates },
              ),
            },
      ),
    }));
  };

  const addField = (stepId: number) => {
    setDirty(true);
    const field: ProfileField = {
      key: `field_${Date.now()}`,
      label: "New Field",
      type: "text",
      required: false,
    };
    setTemplate((prev) => ({
      ...prev,
      steps: prev.steps.map((step) =>
        step.id !== stepId
          ? step
          : { ...step, fields: [...step.fields, field] },
      ),
    }));
  };

  const deleteField = (stepId: number, fieldKey: string) => {
    setDeleteConfirm({
      title: "Delete Field",
      message: "Delete this field?",
      onConfirm: () => {
        setDeleteConfirm(null);
        setDirty(true);
        setTemplate((prev) => ({
          ...prev,
          steps: prev.steps.map((step) =>
            step.id !== stepId
              ? step
              : {
                  ...step,
                  fields: step.fields.filter((f) => f.key !== fieldKey),
                },
          ),
        }));
      },
    });
  };

  const renameTemplate = (index: number, currentName: string) => {
    setRenameModal({ index, value: currentName });
  };

  const executeRename = async () => {
    if (!renameModal) return;
    const { index, value } = renameModal;
    const name = value.trim();
    if (!name || name === templates[index]?.name) {
      setRenameModal(null);
      return;
    }

    const updatedTemplate = { ...templates[index], name };

    // Optimistic update
    setTemplates((prev) =>
      prev.map((t, i) => (i === index ? updatedTemplate : t)),
    );
    setRenameModal(null);

    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/templates/${updatedTemplate.id}/rename`,
        {
          method: "PATCH",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ name }),
        },
      );

      if (!res.ok) throw new Error("Rename failed");
    } catch (e) {
      console.error("Failed to rename template", e);
      // Rollback optimistic update
      setTemplates((prev) =>
        prev.map((t, i) => (i === index ? { ...t, name: templates[index]?.name } : t)),
      );
    }
  };

  /* ===== render ===== */

  // The tab bar is two rows: the fixed ones on top — Overview, Access Log, the
  // system templates and the + — and everything the org has added below them,
  // wrapping onto further rows as it grows. The original index travels with
  // each template, because that's what selection, rename and delete key off.
  const indexed = templates.map((tpl, index) => ({ tpl, index }));
  const systemTabs = indexed.filter(({ tpl }) => isSystemTemplate(tpl.name));
  const customTabs = indexed.filter(({ tpl }) => !isSystemTemplate(tpl.name));

  /**
   * How many people are pointed at a template.
   *
   * A template in use cannot be deleted — the × is not shown at all. Deleting
   * one leaves everybody assigned to it at the Profiles gate with no profile to
   * complete, and it also strands their submitted answers: profileSubmissions
   * carry no orgID and are matched to an org THROUGH the template, so once it is
   * gone there is no signal left to reach them by.
   */
  const assignedTo = (templateId: string | undefined): any[] => {
    if (!templateId) return [];
    const ids = Object.keys(profileAssignments).filter(
      (userId) => profileAssignments[userId] === templateId,
    );
    // Named where the user record is loaded, and by id where it isn't — an
    // assignment to somebody who has since left the org still blocks, and
    // printing the bare id is better than dropping them from the count.
    return ids.map(
      (userId) => users.find((u: any) => u.user_id === userId) ?? { user_id: userId },
    );
  };

  const assignedCount = (templateId: string | undefined): number =>
    assignedTo(templateId).length;

  /**
   * Profiles already FILLED IN against a template, whoever holds it now.
   *
   * Checked separately from the assignment, because the two come apart: unassign
   * somebody and their submitted answers stay exactly where they were. Deleting
   * the template then strands them for good — profileSubmissions carry no orgID
   * and are matched to an org THROUGH profileTemplateId, so once the template is
   * gone there is no signal left to reach them by. That is the same gap the
   * documents check closes in SMRT Docs, and it matters more here: what is
   * stranded is somebody's SIN and work permit.
   */
  const submittedAgainst = (templateId: string | undefined): any[] => {
    if (!templateId) return [];
    return submissions.filter((s: any) => s?.profileTemplateId === templateId);
  };

  /** Whether a template is spoken for at all — assigned to, or filled in. */
  const templateInUse = (templateId: string | undefined): boolean =>
    assignedCount(templateId) > 0 || submittedAgainst(templateId).length > 0;

  /** Names who holds a template, in place of the delete it refuses. */
  const explainInUse = (tpl: { id?: string; name: string }) => {
    const holders = assignedTo(tpl.id);
    const filled = submittedAgainst(tpl.id);
    const nameOf = (userId: string) => {
      const user = users.find((u: any) => u.user_id === userId);
      return user?.name || user?.email || userId;
    };

    // Assignments read first, because that is the case an admin can actually
    // clear. Submissions by somebody no longer assigned are named separately:
    // reassigning nobody would fix them, so the wording has to be honest that
    // the template is being kept for the record rather than for a person.
    const parts: string[] = [];
    if (holders.length > 0) {
      const named = holders
        .slice(0, 5)
        .map((u: any) => u.name || u.email || u.user_id);
      parts.push(
        `assigned to ${holders.length} ${holders.length === 1 ? "person" : "people"}` +
          ` — ${named.join(", ")}${
            holders.length > named.length
              ? `, and ${holders.length - named.length} more`
              : ""
          }`,
      );
    }

    const orphaned = filled.filter(
      (s: any) => profileAssignments[s.userId] !== tpl.id,
    );
    if (orphaned.length > 0) {
      const named = orphaned.slice(0, 5).map((s: any) => nameOf(s.userId));
      parts.push(
        `already filled in by ${orphaned.length} ${
          orphaned.length === 1 ? "person" : "people"
        } who ${orphaned.length === 1 ? "is" : "are"} no longer assigned it` +
          ` — ${named.join(", ")}${
            orphaned.length > named.length
              ? `, and ${orphaned.length - named.length} more`
              : ""
          }`,
      );
    }

    setDeleteConfirm({
      title: "Can't Delete Template",
      message:
        `"${tpl.name}" is ${parts.join(", and ")}.` +
        " Reassign anyone still on it first. Deleting it would leave them at the" +
        " Profiles gate with no profile to complete, and would put every answer" +
        " already submitted against it permanently out of reach.",
      blocked: true,
      onConfirm: () => setDeleteConfirm(null),
    });
  };

  const templateTab = (
    tpl: { id?: string; name: string; data: ProfileTemplate },
    index: number,
  ) => (
    <div
      key={index}
      className={`template-tab ${
        activeTopTab === "templates" && index === activeTemplateIndex
          ? "active"
          : ""
      }`}
      onClick={() => {
        setActiveTopTab("templates");
        setActiveTemplateIndex(index);
        setShowNewTemplatePanel(false);
      }}
      onDoubleClick={() => {
        if (!isSystemTemplate(tpl.name)) renameTemplate(index, tpl.name);
      }}
      onContextMenu={(e) => {
        e.preventDefault();
        if (!isSystemTemplate(tpl.name)) renameTemplate(index, tpl.name);
      }}
    >
      <span className="template-tab-name">{tpl.name}</span>

      {/* A template somebody is assigned loses its × altogether — the strongest
          guard is not offering the action. A padlock takes its place rather than
          nothing, because the admin still needs to find out who is holding it,
          and clicking it is what says so. */}
      {!isSystemTemplate(tpl.name) && templateInUse(tpl.id) && (
        <span
          className="template-tab-locked"
          // Deliberately not a count: a template can be blocked by submitted
          // answers alone, with nobody assigned, and "in use by 0 people" is
          // the kind of nonsense that gets a guard mistrusted.
          title="In use — click to see who"
          onClick={(e) => {
            e.stopPropagation();
            explainInUse(tpl);
          }}
        >
          🔒
        </span>
      )}

      {!isSystemTemplate(tpl.name) && !templateInUse(tpl.id) && (
        <button
          className="template-tab-close"
          onClick={(e) => {
            e.stopPropagation();
            setDeleteConfirm({
              title: "Delete Template",
              message:
                "Are you sure you want to permanently remove this template?",
              onConfirm: () => {
                setDeleteConfirm(null);
                fetch(
                  `${process.env.REACT_APP_BACKEND_URL}/templates/${templates[index].id}`,
                  {
                    method: "DELETE",
                    credentials: "include",
                  },
                );

                setTemplates((prev) => prev.filter((_, i) => i !== index));

                if (activeTemplateIndex === index) {
                  setActiveTemplateIndex(0);
                  setActiveTopTab("templates");
                }
              },
            });
          }}
        >
          ×
        </button>
      )}
    </div>
  );

  return (
    <div className="manage-profiles-page">
      <h1 className="manage-profiles-title">Manage Profiles</h1>

      {/* Traffic light summary */}
      <div style={{ display: "flex", gap: 12, marginBottom: 14, maxWidth: "1100px", margin: "0 auto 14px" }}>
        <div
          title="Needs Admin Action"
          style={{ display: "flex", alignItems: "center", gap: 6, background: "#1c1c1c", border: "1px solid #2a2a2a", borderRadius: 6, padding: "4px 12px", cursor: "pointer" }}
          onClick={() => { setTrafficFilter("red"); setIsTrafficModalOpen(true); }}
        >
          <span style={{ fontSize: "1rem" }}>🔴</span>
          <span style={{ color: "#fff", fontSize: "0.85rem", fontWeight: 600 }}>{getRedItems(users, profileAssignments).length}</span>
        </div>
        <div
          title="Awaiting User Action"
          style={{ display: "flex", alignItems: "center", gap: 6, background: "#1c1c1c", border: "1px solid #2a2a2a", borderRadius: 6, padding: "4px 12px", cursor: "pointer" }}
          onClick={() => { setTrafficFilter("yellow"); setIsTrafficModalOpen(true); }}
        >
          <span style={{ fontSize: "1rem" }}>🟡</span>
          <span style={{ color: "#fff", fontSize: "0.85rem", fontWeight: 600 }}>{getYellowItems(users, profileAssignments, submissions, templates).length}</span>
        </div>
        <div
          title="Ready for Review"
          style={{ display: "flex", alignItems: "center", gap: 6, background: "#1c1c1c", border: "1px solid #2a2a2a", borderRadius: 6, padding: "4px 12px", cursor: "pointer" }}
          onClick={() => { setTrafficFilter("green"); setIsTrafficModalOpen(true); }}
        >
          <span style={{ fontSize: "1rem" }}>🟢</span>
          <span style={{ color: "#fff", fontSize: "0.85rem", fontWeight: 600 }}>{getGreenItems(users, profileAssignments, submissions, templates).length}</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="template-tab-rows">
        <div className="template-tabs">
          <div
            className={`template-tab ${activeTopTab === "overview" ? "active" : ""}`}
            onClick={() => setActiveTopTab("overview")}
          >
            Overview
          </div>
  
          {canViewAccessLog && (
            <div
              className={`template-tab ${
                activeTopTab === "accessLog" ? "active" : ""
              }`}
              onClick={() => setActiveTopTab("accessLog")}
              title="Sensitive reveal audit log (Admin+ / Sensitive)"
            >
              Access Log
            </div>
          )}
  
          {systemTabs.map(({ tpl, index }) => templateTab(tpl, index))}
  
          <div
            // No "active" latch: the + opens a dialog over the editor now rather
            // than replacing it, so the template you were on stays the live tab.
            className="template-tab template-tab-plus"
            onClick={() => {
              setActiveTopTab("templates");
              setShowNewTemplatePanel(true);
            }}
          >
            +
          </div>

          {/* Only offered when there's a row to open — a triangle that reveals
              nothing is worse than no triangle. */}
          {customTabs.length > 0 && (
            <button
              className="template-tabs-toggle"
              title={
                customTabsOpen
                  ? "Hide added templates"
                  : `Show added templates (${customTabs.length})`
              }
              aria-expanded={customTabsOpen}
              onClick={() => setCustomTabsOpen((open) => !open)}
            >
              {customTabsOpen ? "▾" : "▸"}
              {/* Only while closed — once the row is open you can count them. */}
              {!customTabsOpen && (
                <span className="template-tabs-toggle-count">
                  {customTabs.length}
                </span>
              )}
            </button>
          )}
        </div>

        {/* Added templates. Rendered only when there are some, so the tab bar
            doesn't carry an empty row on an org that has none. */}
        {/* Kept mounted so it can animate shut as well as open; the wrapper
            holds it at zero height until the class says otherwise. */}
        {customTabs.length > 0 && (
          <div
            className={`template-tabs-reveal${customTabsOpen ? " open" : ""}`}
            aria-hidden={!customTabsOpen}
          >
            <div className="template-tabs template-tabs-custom">
              {customTabs.map(({ tpl, index }) => templateTab(tpl, index))}
            </div>
          </div>
        )}
      </div>

      {/* Content */}
      {activeTopTab === "overview" && (
        usersLoading ? (
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "24px 16px", color: "#9aa0a6", fontSize: "0.9rem" }}>
            <span className="spinner" aria-hidden="true" />
            Loading users…
          </div>
        ) : usersError ? (
          <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "24px 16px", color: "#e4e4e4", fontSize: "0.9rem" }}>
            <span style={{ color: "#e57373" }}>{usersError}</span>
            <button
              className="template-tab"
              onClick={() => loadApprovedUsers()}
            >
              Retry
            </button>
          </div>
        ) : (
          <Overview
            users={users}
            templates={templates}
            submissions={submissions}
          />
        )
      )}

      {activeTopTab === "accessLog" && canViewAccessLog && (
        <div
          style={{
            background: "#1a1a1a",
            border: "1px solid #2a2a2a",
            borderRadius: 8,
            padding: 16,
            marginTop: 12,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 12,
            }}
          >
            <h3 style={{ margin: 0, fontSize: "1rem" }}>
              Sensitive Access Log
            </h3>
            <span style={{ fontSize: "0.78rem", color: "#9aa0a6" }}>
              {accessLogLoading
                ? "Loading…"
                : `${accessLogEvents.length} event${accessLogEvents.length === 1 ? "" : "s"}`}
            </span>
          </div>

          {accessLogError && (
            <div
              style={{
                color: "#ef9a9a",
                background: "rgba(229, 57, 53, 0.12)",
                padding: "8px 10px",
                borderRadius: 4,
                marginBottom: 12,
                fontSize: "0.85rem",
              }}
            >
              {accessLogError}
            </div>
          )}

          {!accessLogLoading && accessLogEvents.length === 0 && !accessLogError && (
            <div
              style={{
                color: "#9aa0a6",
                fontSize: "0.85rem",
                textAlign: "center",
                padding: "20px 0",
              }}
            >
              No sensitive-field reveals recorded for this organisation yet.
            </div>
          )}

          {accessLogEvents.length > 0 && (
            <div style={{ overflowX: "auto" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: "0.82rem",
                  color: "#e4e4e4",
                }}
              >
                <thead>
                  <tr style={{ textAlign: "left", color: "#9aa0a6" }}>
                    <th style={{ padding: "8px 10px", borderBottom: "1px solid #2a2a2a", whiteSpace: "nowrap" }}>When</th>
                    <th style={{ padding: "8px 10px", borderBottom: "1px solid #2a2a2a" }}>Employee</th>
                    <th style={{ padding: "8px 10px", borderBottom: "1px solid #2a2a2a" }}>Template</th>
                    <th style={{ padding: "8px 10px", borderBottom: "1px solid #2a2a2a" }}>Field</th>
                    <th style={{ padding: "8px 10px", borderBottom: "1px solid #2a2a2a" }}>Revealed By</th>
                    <th style={{ padding: "8px 10px", borderBottom: "1px solid #2a2a2a" }}>Reason</th>
                    <th style={{ padding: "8px 10px", borderBottom: "1px solid #2a2a2a", whiteSpace: "nowrap" }}>IP</th>
                    <th style={{ padding: "8px 10px", borderBottom: "1px solid #2a2a2a" }}>User Agent</th>
                  </tr>
                </thead>
                <tbody>
                  {accessLogEvents.map((e, i) => {
                    const employee = users.find((u: any) => u.user_id === e.userId);
                    const employeeLabel = employee?.name || employee?.email || e.userId;
                    const revealer = users.find((u: any) => u.user_id === e.revealedByUserID);
                    const revealerLabel =
                      revealer?.name || revealer?.email || e.revealedByUserID;
                    const when = e.timestamp
                      ? new Date(e.timestamp).toLocaleString()
                      : "—";
                    return (
                      <tr key={i} style={{ borderBottom: "1px solid #222" }}>
                        <td style={{ padding: "8px 10px", whiteSpace: "nowrap" }}>{when}</td>
                        <td style={{ padding: "8px 10px" }} title={e.userId}>{employeeLabel}</td>
                        <td style={{ padding: "8px 10px" }}>{e.templateName || "—"}</td>
                        <td style={{ padding: "8px 10px", fontFamily: "monospace", fontSize: "0.78rem" }}>{e.fieldKey || "—"}</td>
                        <td style={{ padding: "8px 10px" }} title={e.revealedByUserID}>{revealerLabel}</td>
                        <td style={{ padding: "8px 10px", maxWidth: 260 }}>{e.reason || "—"}</td>
                        <td style={{ padding: "8px 10px", whiteSpace: "nowrap", fontFamily: "monospace", fontSize: "0.78rem", color: "#9aa0a6" }}>{e.ipAddress || "—"}</td>
                        <td style={{ padding: "8px 10px", maxWidth: 280, fontSize: "0.75rem", color: "#9aa0a6" }} title={e.userAgent}>
                          {e.userAgent
                            ? e.userAgent.length > 60
                              ? e.userAgent.slice(0, 60) + "…"
                              : e.userAgent
                            : "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTopTab === "templates" && (
        <>
          <TemplateEditor
            template={template}
            editingStepId={editingStepId}
            dirty={dirty}
            startEdit={startEdit}
            cancelEdit={cancelEdit}
            saveEdit={saveEdit}
            updateStepLabel={updateStepLabel}
            moveStep={moveStep}
            deleteStep={deleteStep}
            reorderField={reorderField}
            updateField={updateField}
            addField={addField}
            deleteField={deleteField}
            openFormatFields={openFormatFields}
            setOpenFormatFields={setOpenFormatFields}
            openVisibilityFields={openVisibilityFields}
            setOpenVisibilityFields={setOpenVisibilityFields}
          />
          <div className="add-step-container">
            <button
              className="btn-add btn-add-step"
              onClick={() => setAddStepModal({ scope: "public" })}
            >
              + Add Step
            </button>
          </div>
        </>
      )}
      <ProfilesTrafficModal
        isOpen={isTrafficModalOpen}
        onClose={() => setIsTrafficModalOpen(false)}
        filter={trafficFilter}
        users={users}
        assignments={profileAssignments}
        submissions={submissions}
        templates={templates}
      />
      <DeleteConfirmModal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={deleteConfirm?.onConfirm || (() => {})}
        title={deleteConfirm?.title || ""}
        message={deleteConfirm?.message || ""}
        // A refusal has nothing to confirm, so the red Delete becomes a grey OK.
        // Same mapping ManageSMRTDocs uses, so the two read alike.
        confirmLabel={deleteConfirm?.blocked ? "OK" : "Delete"}
        confirmColor={deleteConfirm?.blocked ? "#4b5563" : undefined}
      />
      {showNewTemplatePanel && (
        <div className="smrtdocs-modal-overlay">
          <div className="smrtdocs-modal" style={{ width: "480px" }}>
            <div className="smrtdocs-modal-header">
              <h2 style={{ margin: 0, fontSize: "1rem" }}>Create New Template</h2>
            </div>
            <div className="smrtdocs-modal-body">
              <p style={{ marginTop: 0 }}>Select how you want to start.</p>
              {/* Each hands off to its own modal and closes this one, so only
                  one dialog is ever up; Cancel there brings this one back. */}
              <div className="new-template-actions">
                <button
                  className="btn-add"
                  onClick={() => {
                    setNewTemplateError(null);
                    setNewTemplateName("");
                    setShowNewTemplatePanel(false);
                  }}
                >
                  Add New
                </button>
                <button
                  className="btn-add"
                  onClick={() => {
                    setDuplicateError(null);
                    setDuplicateModal({ sourceIndex: activeTemplateIndex });
                    setShowNewTemplatePanel(false);
                  }}
                >
                  Duplicate Existing
                </button>
              </div>
              <div style={{ display: "flex", justifyContent: "flex-start", marginTop: "24px" }}>
                <button
                  className="btn-cancel"
                  onClick={() => setShowNewTemplatePanel(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {saveError && (
        <div className="smrtdocs-modal-overlay">
          <div className="smrtdocs-modal" style={{ width: "480px" }}>
            <div className="smrtdocs-modal-header">
              <h2 style={{ margin: 0, fontSize: "1rem" }}>Couldn't Save</h2>
            </div>
            <div className="smrtdocs-modal-body">
              <p style={{ marginTop: 0, marginBottom: "20px" }}>{saveError}</p>
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <button className="btn-save" onClick={() => setSaveError(null)}>
                  OK
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {duplicateModal && (
        <div className="smrtdocs-modal-overlay">
          <div className="smrtdocs-modal" style={{ width: "480px" }}>
            <div className="smrtdocs-modal-header">
              <h2 style={{ margin: 0, fontSize: "1rem" }}>Duplicate Template</h2>
            </div>
            <div className="smrtdocs-modal-body">
              <label
                htmlFor="duplicate-template-source"
                style={{ display: "block", marginBottom: 6 }}
              >
                Which template do you want to duplicate?
              </label>
              <select
                id="duplicate-template-source"
                className="manage-input"
                value={duplicateModal.sourceIndex}
                autoFocus
                disabled={duplicating}
                onChange={(e) =>
                  setDuplicateModal({ sourceIndex: Number(e.target.value) })
                }
                style={{ width: "100%", marginBottom: duplicateError ? "8px" : "20px" }}
              >
                {templates.map((t, i) => (
                  <option key={t.id ?? i} value={i}>
                    {t.name}
                  </option>
                ))}
              </select>
              {duplicateError && (
                <div style={{ color: "#e57373", fontSize: "0.8rem", marginBottom: "16px" }}>
                  {duplicateError}
                </div>
              )}
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <button
                  className="btn-cancel"
                  disabled={duplicating}
                  onClick={() => {
                    setDuplicateModal(null);
                    setDuplicateError(null);
                    setShowNewTemplatePanel(true);
                  }}
                >
                  Cancel
                </button>
                <button
                  className="btn-save"
                  disabled={duplicating}
                  onClick={duplicateTemplate}
                >
                  {duplicating ? "Duplicating…" : "Duplicate"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {addStepModal && (
        <div className="smrtdocs-modal-overlay">
          <div className="smrtdocs-modal" style={{ width: "480px" }}>
            <div className="smrtdocs-modal-header">
              <h2 style={{ margin: 0, fontSize: "1rem" }}>Add Step</h2>
            </div>
            <div className="smrtdocs-modal-body">
              <label
                htmlFor="add-step-scope"
                style={{ display: "block", marginBottom: 6 }}
              >
                Step Type
              </label>
              <select
                id="add-step-scope"
                className="manage-input"
                value={addStepModal.scope}
                autoFocus
                onChange={(e) =>
                  setAddStepModal({
                    scope: e.target.value === "internal" ? "internal" : "public",
                  })
                }
                style={{ width: "100%", marginBottom: "20px" }}
              >
                <option value="public">Public — user completes</option>
                <option value="internal">Internal — admin only</option>
              </select>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <button className="btn-cancel" onClick={() => setAddStepModal(null)}>
                  Cancel
                </button>
                <button className="btn-save" onClick={addStep}>
                  Add Step
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {newTemplateName !== null && (
        <div className="smrtdocs-modal-overlay">
          <div className="smrtdocs-modal" style={{ width: "480px" }}>
            <div className="smrtdocs-modal-header">
              <h2 style={{ margin: 0, fontSize: "1rem" }}>New Template</h2>
            </div>
            <div className="smrtdocs-modal-body">
              <label
                htmlFor="new-template-name"
                style={{ display: "block", marginBottom: 6 }}
              >
                Template Name
              </label>
              <input
                id="new-template-name"
                className="manage-input"
                value={newTemplateName}
                autoFocus
                disabled={creatingTemplate}
                onChange={(e) => setNewTemplateName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") createBlankTemplate();
                }}
                style={{ width: "100%", marginBottom: newTemplateError ? "8px" : "20px" }}
              />
              {newTemplateError && (
                <div style={{ color: "#e57373", fontSize: "0.8rem", marginBottom: "16px" }}>
                  {newTemplateError}
                </div>
              )}
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <button
                  className="btn-cancel"
                  disabled={creatingTemplate}
                  onClick={() => {
                    setNewTemplateName(null);
                    setNewTemplateError(null);
                    setShowNewTemplatePanel(true);
                  }}
                >
                  Cancel
                </button>
                {/* Grey until there's a name, and while the save is in flight —
                    a still button on a slow action gets clicked twice. */}
                <button
                  className="btn-save"
                  disabled={!newTemplateName.trim() || creatingTemplate}
                  onClick={createBlankTemplate}
                >
                  {creatingTemplate ? "Creating…" : "Create"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {renameModal && (
        <div className="smrtdocs-modal-overlay">
          <div className="smrtdocs-modal" style={{ width: "480px" }}>
            <div className="smrtdocs-modal-header">
              <h2 style={{ margin: 0, fontSize: "1rem" }}>Rename Template</h2>
            </div>
            <div className="smrtdocs-modal-body">
              <input
                className="manage-input"
                value={renameModal.value}
                autoFocus
                onChange={(e) => setRenameModal({ ...renameModal, value: e.target.value })}
                onKeyDown={(e) => { if (e.key === "Enter") executeRename(); }}
                style={{ width: "100%", marginBottom: "20px" }}
              />
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <button className="btn-cancel" onClick={() => setRenameModal(null)}>Cancel</button>
                <button className="btn-save" onClick={executeRename}>Save</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
